import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import styles from "./ImagePreviewModal.module.less";

interface ImageItem {
  url: string;
  alt?: string;
}

interface ImagePreviewModalProps {
  images: ImageItem[];
  visible: boolean;
  currentIndex?: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

export default function ImagePreviewModal({
  images = [],
  visible,
  currentIndex: initialIndex = 0,
  onClose,
  onIndexChange,
}: ImagePreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loaded, setLoaded] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [offsetX, setOffsetX] = useState(0);
  const startXRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      setLoaded(false);
      setIsZoomed(false);
      setDragging(false);
      setOffsetX(0);
    } else {
      // Reset all states when modal closes
      setLoaded(false);
      setIsZoomed(false);
      setDragging(false);
      setOffsetX(0);
    }
  }, [visible, initialIndex]);

  useEffect(() => {
    setLoaded(false);
  }, [currentIndex, visible]);

  useEffect(() => {
    if (!visible) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, currentIndex, images.length]);

  const goto = useCallback(
    (index: number) => {
      const safe = Math.max(0, Math.min(index, images.length - 1));
      if (safe !== currentIndex) {
        setCurrentIndex(safe);
        onIndexChange?.(safe);
      }
    },
    [currentIndex, images.length, onIndexChange]
  );

  const next = useCallback(() => {
    goto(currentIndex + 1);
  }, [currentIndex, goto]);

  const prev = useCallback(() => {
    goto(currentIndex - 1);
  }, [currentIndex, goto]);

  // Pointer / touch handlers for swipe
  const onPointerDown = (clientX: number) => {
    startXRef.current = clientX;
    setDragging(true);
    setOffsetX(0);
  };

  const onPointerMove = (clientX: number) => {
    if (!dragging || startXRef.current === null) return;
    setOffsetX(clientX - startXRef.current);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    const delta = offsetX;
    const abs = Math.abs(delta);
    const threshold = (containerRef.current?.clientWidth || 300) * 0.15; // 15%

    if (abs > threshold) {
      if (delta > 0) prev();
      else next();
    }

    // reset
    setOffsetX(0);
    setDragging(false);
    startXRef.current = null;
  };

  // Double click toggles zoom
  const onDoubleClick = () => setIsZoomed((z) => !z);

  const currentImage = images[currentIndex];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable
      closeIcon={
        <CloseOutlined
          className={styles.closeIcon}
          style={{ color: "white", fontSize: 20 }}
        />
      }
      width="90%"
      centered
      styles={{
        body: {
          padding: 0,
          maxHeight: "90vh",
          overflow: "hidden",
          backgroundColor: "rgba(0,0,0,0.70)",
          borderRadius: "16px"
        }
      }}
      style={{ top: 0, maxHeight: "100vh" }}
      className={styles.modal}
    >
      <div
        ref={containerRef}
        className={styles.container}
        onMouseDown={(e) => onPointerDown(e.clientX)}
        onMouseMove={(e) => {
          if (e.buttons !== 1) return;
          onPointerMove(e.clientX);
        }}
        onMouseUp={() => onPointerUp()}
        onTouchStart={(e) => onPointerDown(e.touches[0].clientX)}
        onTouchMove={(e) => onPointerMove(e.touches[0].clientX)}
        onTouchEnd={() => onPointerUp()}
      >
        {/* Left / Right overlay buttons */}
        {currentIndex > 0 && (
          <button
            aria-label="prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className={classNames(styles.navButton, styles.navButtonPrev)}
          >
            <LeftOutlined />
          </button>
        )}

        {currentIndex < images.length - 1 && (
          <button
            aria-label="next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className={classNames(styles.navButton, styles.navButtonNext)}
          >
            <RightOutlined />
          </button>
        )}

        {/* Top controls: counter + zoom */}
        <div className={styles.topControls}>
          <div className={styles.counter}>
            {currentIndex + 1} / {images.length}
          </div>
          <button
            className={styles.zoomButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsZoomed((z) => !z);
            }}
            title={isZoomed ? "Thu nhỏ" : "Phóng to"}
          >
            {isZoomed ? <ZoomOutOutlined /> : <ZoomInOutlined />}
          </button>
        </div>

        {/* Loading spinner */}
        {!loaded && (
          <div className={styles.loadingWrapper}>
            <Spin size="large" />
          </div>
        )}

        {/* Image area */}
        <div
          className={styles.imageWrapper}
          onDoubleClick={(e) => {
            e.stopPropagation();
            onDoubleClick();
          }}
        >
          {currentImage ? (
            <img
              key={`${currentImage.url}-${currentIndex}-${visible}`}
              src={currentImage.url}
              alt={currentImage.alt || `Image ${currentIndex + 1}`}
              onLoad={() => setLoaded(true)}
              onError={() => setLoaded(true)}
              ref={(imgElement) => {
                if (imgElement && imgElement.complete && imgElement.naturalWidth > 0) {
                  setLoaded(true);
                }
              }}
              className={classNames(styles.image, {
                [styles.imageZoomed]: isZoomed,
                [styles.imageDragging]: dragging,
                [styles.imageLoaded]: loaded,
                [styles.imageLoading]: !loaded,
              })}
              style={{
                transform: `translateX(${offsetX}px) ${isZoomed ? "scale(1.9)" : "scale(1)"}`,
              }}
              draggable={false}
            />
          ) : (
            <div className={styles.noImage}>Không có ảnh</div>
          )}
        </div>

        {/* Thumbnails strip */}
        {images.length > 1 && (
          <div className={styles.thumbnailsContainer}>
            <div className={styles.thumbnailsWrapper}>
              {images.map((it, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    goto(idx);
                  }}
                  className={classNames(styles.thumbnailButton, {
                    [styles.thumbnailButtonActive]: idx === currentIndex,
                  })}
                >
                  <img
                    src={it.url}
                    alt={it.alt || `thumb-${idx}`}
                    className={styles.thumbnailImage}
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
